import { useState } from 'react'
import { useCollection } from '@cloudscape-design/collection-hooks'
import Badge from '@cloudscape-design/components/badge'
import Box from '@cloudscape-design/components/box'
import Cards from '@cloudscape-design/components/cards'
import ColumnLayout from '@cloudscape-design/components/column-layout'
import Header from '@cloudscape-design/components/header'
import Pagination from '@cloudscape-design/components/pagination'
import SpaceBetween from '@cloudscape-design/components/space-between'
import StatusIndicator from '@cloudscape-design/components/status-indicator'
import Table from '@cloudscape-design/components/table'
import type { StatusIndicatorProps } from '@cloudscape-design/components/status-indicator'
import type { RunSummaryGql } from '@/lib/types'
import { useListRuns } from '@/hooks/useRuns'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { formatRunDate } from '@/utils/format'
import { RunNodeSteps } from '@/pages/clients/ClientAgent/RunNodeSteps'

interface Props {
  clientId: string
}

// Augment RunSummaryGql with sentinel fields used to inject expandable detail rows.
type RunRow = RunSummaryGql & {
  _isDetailRow?: boolean
  _parentId?: string
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const STATUS_TYPE_MAP: Record<'running' | 'complete' | 'error' | 'stopped', StatusIndicatorProps['type']> = {
  complete: 'success',
  error: 'error',
  running: 'in-progress',
  stopped: 'stopped',
}

export function formatDuration(startedAt: string, completedAt: string | null): string {
  if (!completedAt) return '—'
  const ms = new Date(completedAt).getTime() - new Date(startedAt).getTime()
  if (ms < 0) return '—'
  const totalSeconds = Math.floor(ms / 1000)
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`
}

// Build a sentinel detail row for a given parent run row
function makeDetailRow(parent: RunRow): RunRow {
  return {
    ...parent,
    id: `${parent.id}__detail`,
    _isDetailRow: true,
    _parentId: parent.id,
  }
}

// ─── Component ────────────────────────────────────────────────────────────────

export function RunHistorySection({ clientId }: Props) {
  const { runs, loading } = useListRuns(clientId)
  const rows: RunRow[] = runs ?? []
  const isMobile = useMediaQuery('(max-width: 1023px)')

  const [expandedItems, setExpandedItems] = useState<RunRow[]>([])

  const { items, collectionProps, paginationProps } = useCollection(rows, {
    sorting: {
      defaultState: {
        sortingColumn: { sortingField: 'startedAt' },
        isDescending: true,
      },
    },
    pagination: { pageSize: isMobile ? 10 : 20 },
  })

  if (isMobile) {
    return (
      <SpaceBetween size="m">
        <Cards<RunRow>
          {...collectionProps}
          items={items}
          loading={loading}
          loadingText="Loading run history..."
          cardsPerRow={[{ cards: 1 }]}
          header={
            <Header
              variant="h2"
              description="Every time your agent ran"
            >
              Run History
            </Header>
          }
          cardDefinition={{
            header: (item) => (
              <SpaceBetween direction="horizontal" size="xs" alignItems="center">
                <Box variant="strong">{formatRunDate(item.startedAt)}</Box>
                {item.trigger
                  ? item.trigger.toLowerCase() === 'auto'
                    ? <Badge color="blue">Auto</Badge>
                    : <Badge color="grey">Manual</Badge>
                  : null}
                <StatusIndicator type={STATUS_TYPE_MAP[item.status] ?? 'pending'}>
                  {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                </StatusIndicator>
              </SpaceBetween>
            ),
            sections: [
              {
                id: 'stats',
                content: (item) => (
                  <ColumnLayout columns={3} variant="text-grid">
                    <SpaceBetween size="xxs">
                      <Box variant="awsui-key-label">Duration</Box>
                      <Box>{formatDuration(item.startedAt, item.completedAt)}</Box>
                    </SpaceBetween>
                    <SpaceBetween size="xxs">
                      <Box variant="awsui-key-label">Content</Box>
                      <Box>{item.resultsCount ?? '—'}</Box>
                    </SpaceBetween>
                    <SpaceBetween size="xxs">
                      <Box variant="awsui-key-label">Cost</Box>
                      <Box>
                        {item.estimatedCostUsd != null
                          ? `$${item.estimatedCostUsd.toFixed(2)}`
                          : '—'}
                      </Box>
                    </SpaceBetween>
                  </ColumnLayout>
                ),
              },
            ],
          }}
          empty={
            <Box color="text-body-secondary" textAlign="center" padding="xl">
              No runs yet. Your agent will appear here after its first scheduled run.
            </Box>
          }
        />
        <Pagination {...paginationProps} />
      </SpaceBetween>
    )
  }

  return (
    <Table<RunRow>
      {...collectionProps}
      loading={loading}
      loadingText="Loading run history..."
      trackBy="id"
      header={
        <Header
          variant="h2"
          description="Every time your agent ran — expand a row to see step-by-step detail"
        >
          Run History
        </Header>
      }
      empty={
        <Box color="text-body-secondary" textAlign="center" padding="xl">
          No runs yet. Your agent will appear here after its first scheduled run.
        </Box>
      }
      expandableRows={{
        // Return a single detail sentinel child for each expandable run row
        getItemChildren: (item) => (item._isDetailRow ? [] : [makeDetailRow(item)]),
        isItemExpandable: (item) => !item._isDetailRow,
        expandedItems,
        onExpandableItemToggle: ({ detail }) => {
          setExpandedItems((prev) =>
            detail.expanded
              ? [...prev, detail.item]
              : prev.filter((r) => r.id !== detail.item.id),
          )
        },
      }}
      columnDefinitions={[
        {
          id: 'date',
          header: 'Date',
          cell: (item) =>
            item._isDetailRow ? (
              <RunNodeSteps
                clientId={clientId}
                runId={item._parentId ?? item.id}
                errorMessage={item.errorMessage}
              />
            ) : (
              formatRunDate(item.startedAt)
            ),
          sortingField: 'startedAt',
          width: 160,
        },
        {
          id: 'type',
          header: 'Type',
          cell: (item) => {
            if (item._isDetailRow) return null
            if (!item.trigger) return <Box color="text-body-secondary" variant="span">—</Box>
            return item.trigger.toLowerCase() === 'auto'
              ? <Badge color="blue">Auto</Badge>
              : <Badge color="grey">Manual</Badge>
          },
          width: 100,
        },
        {
          id: 'status',
          header: 'Status',
          cell: (item) =>
            item._isDetailRow ? null : (
              <StatusIndicator type={STATUS_TYPE_MAP[item.status] ?? 'pending'}>
                {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
              </StatusIndicator>
            ),
          sortingField: 'status',
          width: 140,
        },
        {
          id: 'duration',
          header: 'Duration',
          cell: (item) =>
            item._isDetailRow ? null : formatDuration(item.startedAt, item.completedAt),
          width: 100,
        },
        {
          id: 'content',
          header: 'Content',
          cell: (item) => (item._isDetailRow ? null : item.resultsCount),
          sortingField: 'resultsCount',
          width: 90,
        },
        {
          id: 'cost',
          header: 'Cost',
          cell: (item) =>
            item._isDetailRow
              ? null
              : item.estimatedCostUsd != null
                ? `$${item.estimatedCostUsd.toFixed(2)}`
                : '—',
          sortingField: 'estimatedCostUsd',
          width: 90,
        },
      ]}
      items={items}
    />
  )
}
